import {
    ApplicationConfig,
    inject,
    provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideApollo } from 'apollo-angular';
import { ApolloLink } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
    withXhr,
} from '@angular/common/http';
import { InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { UserService } from './services/userService';
import { MarketplaceProductService } from './services/marketplace/productServices';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { environment } from 'src/environments/environment';
import { LoadingInterceptor } from './htp-interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { LoginService } from './services/utility/login.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes),
        provideApollo(() => {
            const httpLink = inject(HttpLink);
            const login = inject(LoginService);
            return createApollo(httpLink, login);
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingInterceptor,
            multi: true,
        },
        UserService,
        MarketplaceProductService,
        provideAnimationsAsync(),
        provideMomentDateAdapter(),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: 'dynamic',
            },
        },
    ],
};

export function createApollo(httpLink: HttpLink, login: LoginService) {
    const basic = setContext((_operation, _context) => ({
        headers: {
            Accept: 'application/json; charset=utf-8',
        },
    }));

    const link = ApolloLink.from([
        basic,
        httpLink.create({
            uri: environment.marketplaceHost,
            withCredentials: true,
        }),
    ]);
    const logoutLink = new ErrorLink(({ error }) => {
        if (!CombinedGraphQLErrors.is(error)) return;
        for (const graphQLError of error.errors) {
            if (
                graphQLError.extensions?.code === 'AUTH_NOT_AUTHENTICATED' ||
                graphQLError.extensions?.code === 'AUTHENTICATION_FAILED'
            ) {
                login.openLoginPopUp();
            }
            if (graphQLError.extensions?.code === 'AUTH_NOT_AUTHORIZED') {
                sessionStorage.setItem(
                    'lastError',
                    JSON.stringify(graphQLError, null, 2),
                );
                sessionStorage.setItem('deniedUrl', location.pathname);
                location.replace('/error/auth');
            }
        }
    });

    const cache = new InMemoryCache();

    return {
        link: logoutLink.concat(link),
        cache,
        connectToDevTools: true,
    };
}
